// 合計グリッドの行生成と反映制御をまとめる service です。
export function createTotalGridService(deps) {
    const {
        PANEL_TYPE,
        SIJISU_TOTAL_COL_ID,
        isEmpty,
        DicVal,
        normalizeTypeKey,
        measureTimedLoop,
        panel,
        GridData,
        entrydataRef,
        currentNormalSectionDefs,
        currentSampleSectionDefs,
        allCurrentSectionDefs,
        resolveSectionIdByGridPanel,
        resolveGridSectionPanel,
        resolveTotalHostPanel,
        sectionTotalType,
        normalizeTotalTypeForColumns,
        buildTotalPanel,
        getDetailRowsByPanelType,
        pickTotalRows,
        buildGenkaSummaryRowsFromFlatData,
        normalizeGenkaSummaryRows,
        ensureTotalDataRef,
        TotalGridApi,
        SetTotalGridApi,
        setGridRowsByApi,
        hideGridAuxColumns,
        suppressTotalValueChangedRef,
        canonicalPanelType
    } = deps;

    // マウント済み合計グリッドへ rowData を直接反映します。
    function applyTotalRowsNow(totalPanel, rows) {
        try {
            const api = TotalGridApi(totalPanel);
            if (isEmpty(api)) return;
            suppressTotalValueChangedRef.current = true;
            setGridRowsByApi(api, Array.isArray(rows) ? rows : []);
            if (typeof api.refreshCells === "function") api.refreshCells({ force: true });
            if (typeof api.redrawRows === "function") api.redrawRows();
            if (typeof api.refreshHeader === "function") api.refreshHeader();
        } catch (error) {
            console.error(error);
        } finally {
            suppressTotalValueChangedRef.current = false;
        }
    }

    // 合計グリッド初回描画時に API 保存と rowData 初期反映を行います。
    function onFirstTotalDataRendered(param, totalPanel) {
        try {
            if (isEmpty(param) || isEmpty(param.api) || isEmpty(totalPanel)) return;
            SetTotalGridApi(totalPanel, param.api);
            if (typeof param.api.setGridOption === "function") {
                param.api.setGridOption("rowNumbers", false);
            }
            hideGridAuxColumns(param.api);
            const isGenkaTotal = String(resolveSectionIdByGridPanel(totalPanel) ?? "") === "genka"
                || normalizeTypeKey(DicVal(totalPanel, "totaltype", "")) === normalizeTypeKey(PANEL_TYPE.GENKA_SUMMARY);
            const rows = isGenkaTotal
                ? normalizeGenkaSummaryRows(ensureTotalDataRef(totalPanel).current)
                : (Array.isArray(ensureTotalDataRef(totalPanel).current) ? ensureTotalDataRef(totalPanel).current : []);
            ensureTotalDataRef(totalPanel).current = rows;
            applyTotalRowsNow(totalPanel, rows);
            if (isGenkaTotal) {
                if (typeof param.api.refreshCells === "function") param.api.refreshCells({ force: true });
                if (typeof param.api.redrawRows === "function") param.api.redrawRows();
            }
        } catch (error) {
            console.error(error);
        }
    }

    // 合計グリッドが空のときに返す既定行を返します。
    function defaultTotalRows() {
        return [{ rowid: "TOTAL", grip: "計", __displayOnly: true }];
    }

    // 105 の合計行が有効か判定します。
    function hasValidSijisuTotalRows(rows) {
        if (!Array.isArray(rows) || rows.length === 0) return false;
        return rows.some((row) => {
            if (isEmpty(row) || typeof row !== "object") return false;
            return Object.prototype.hasOwnProperty.call(row, SIJISU_TOTAL_COL_ID);
        });
    }

    // 指示数(type=104)の明細から数量計(type=105)の合計行を作成します。
    function buildSijisuTotalRows(detailData) {
        const rows = Array.isArray(detailData?.[PANEL_TYPE.SIJISU]) ? detailData[PANEL_TYPE.SIJISU] : [];
        const toNum = (value) => {
            const num = Number(String(value ?? "").replace(/,/g, ""));
            return Number.isFinite(num) ? num : 0;
        };
        const qtyTotal = rows.reduce((sum, row) => sum + toNum(DicVal(row, "SSDM_SEIHATCHUSU", "")), 0);
        return [{
            rowid: "TOTAL",
            grip: "計",
            [SIJISU_TOTAL_COL_ID]: String(qtyTotal)
        }];
    }

    // sample 明細から合計行を構築します。
    function buildSampleTotalRows(detailData) {
        const totalRows = getDetailRowsByPanelType(detailData, PANEL_TYPE.SAMPLE_TOTAL);
        if (Array.isArray(totalRows) && totalRows.some((row) => Object.prototype.hasOwnProperty.call(row || {}, "SAMPLE_SURYO_TOTAL"))) {
            return totalRows;
        }
        const detailRows = getDetailRowsByPanelType(detailData, PANEL_TYPE.SAMPLE_DETAIL);
        const toNum = (value) => {
            const num = Number(String(value ?? "").replace(/,/g, ""));
            return Number.isFinite(num) ? num : 0;
        };
        const qtyTotal = detailRows.reduce((sum, row) => sum + toNum(DicVal(row, "SSDSPM_SURYO", "")), 0);
        return [{
            rowid: "TOTAL",
            grip: "計",
            SAMPLE_SURYO_TOTAL: String(qtyTotal)
        }];
    }

    // 合計パネルへ明細合計を反映します。
    function applyTotalRowsToPanel(totalPanel, totalType, sectionId, detailData) {
        const totalData = pickTotalRows(detailData, totalType, sectionId);
        if (sectionId === "sijisu") {
            const nextRows = hasValidSijisuTotalRows(totalData)
                ? totalData
                : buildSijisuTotalRows(detailData);
            ensureTotalDataRef(totalPanel).current = nextRows;
            applyTotalRowsNow(totalPanel, nextRows);
            return;
        }
        if (sectionId === "sample_info") {
            const nextRows = buildSampleTotalRows(detailData);
            ensureTotalDataRef(totalPanel).current = nextRows;
            applyTotalRowsNow(totalPanel, nextRows);
            return;
        }
        if (!isEmpty(totalData)) {
            const nextRows = sectionId === "genka"
                ? normalizeGenkaSummaryRows(totalData)
                : totalData;
            ensureTotalDataRef(totalPanel).current = nextRows;
            applyTotalRowsNow(totalPanel, nextRows);
            return;
        }
        if (sectionId === "genka") {
            const rebuiltTotal = buildGenkaSummaryRowsFromFlatData(entrydataRef.current);
            if (Array.isArray(rebuiltTotal) && rebuiltTotal.length !== 0) {
                const nextRows = normalizeGenkaSummaryRows(rebuiltTotal);
                ensureTotalDataRef(totalPanel).current = nextRows;
                applyTotalRowsNow(totalPanel, nextRows);
                return;
            }
        }
        const defaultRows = defaultTotalRows();
        ensureTotalDataRef(totalPanel).current = defaultRows;
        applyTotalRowsNow(totalPanel, defaultRows);
    }

    // panel 定義ベースの totaltype パネルへ合計行を反映します。
    function applyTotalRowsByPanelDefinition(detailData) {
        measureTimedLoop(
            panel.filter((panelInfo) => !isEmpty(panelInfo.totaltype)),
            (panelInfo, index) => {
                const sectionId = resolveSectionIdByGridPanel(panelInfo);
                const panelType = canonicalPanelType(DicVal(panelInfo, "type", ""));
                const panelId = String(DicVal(panelInfo, "id", index));
                return `${sectionId || panelId}:${panelType}`;
            },
            (panelInfo) => {
                const section = allCurrentSectionDefs()
                    .find((sec) => String(sec.panelType ?? "") === String(panelInfo.type ?? "") || String(sec.panelId ?? "") === String(panelInfo.id ?? ""));
                const sectionId = isEmpty(section) ? "" : String(section.id ?? "");
                applyTotalRowsToPanel(panelInfo, panelInfo.totaltype, sectionId, detailData);
            },
            "applyTotalRowsByPanelDefinition"
        );
    }

    // section 定義ベースの totaltype パネルへ合計行を反映します。
    function applyTotalRowsBySectionDefinition(detailData) {
        measureTimedLoop(
            allCurrentSectionDefs().filter((section) => !isEmpty(section.totalType)),
            (section, index) => String(DicVal(section, "id", index)),
            (section) => {
                const panelInfo = resolveTotalHostPanel(section, resolveGridSectionPanel(section));
                if (isEmpty(panelInfo)) return;
                const totalType = normalizeTotalTypeForColumns(sectionTotalType(section, panelInfo), section.id);
                const totalPanel = { ...panelInfo, totaltype: totalType };
                applyTotalRowsToPanel(totalPanel, totalType, section.id, detailData);
            },
            "applyTotalRowsBySectionDefinition"
        );
    }

    // 104 編集時に 105 の合計行だけを即時再計算します。
    function recalcSijisuTotalRows(panelInfo) {
        try {
            const section = currentNormalSectionDefs().find((sec) => sec.id === "sijisu");
            if (isEmpty(section)) return;
            const gridPanel = isEmpty(panelInfo) ? resolveGridSectionPanel(section) : panelInfo;
            if (isEmpty(gridPanel)) return;
            const currentRows = GridData(gridPanel);
            const nextRows = buildSijisuTotalRows({ [PANEL_TYPE.SIJISU]: currentRows });
            const totalType = sectionTotalType(section, gridPanel);
            const { totalPanel } = buildTotalPanel(section, gridPanel, totalType);
            ensureTotalDataRef(totalPanel).current = nextRows;
            applyTotalRowsNow(totalPanel, nextRows);
        } catch (error) {
            console.error(error);
        }
    }

    // 135 編集時に 136 の合計行だけを即時再計算します。
    function recalcSampleTotalRows(panelInfo) {
        try {
            const section = currentSampleSectionDefs().find((sec) => sec.id === "sample_info");
            if (isEmpty(section)) return;
            const gridPanel = isEmpty(panelInfo) ? resolveGridSectionPanel(section) : panelInfo;
            if (isEmpty(gridPanel)) return;
            const currentRows = GridData(gridPanel);
            const nextRows = buildSampleTotalRows({ [PANEL_TYPE.SAMPLE_DETAIL]: currentRows });
            const totalType = sectionTotalType(section, gridPanel);
            const { totalPanel } = buildTotalPanel(section, gridPanel, totalType);
            ensureTotalDataRef(totalPanel).current = nextRows;
            applyTotalRowsNow(totalPanel, nextRows);
        } catch (error) {
            console.error(error);
        }
    }

    return {
        applyTotalRowsNow,
        onFirstTotalDataRendered,
        recalcSijisuTotalRows,
        recalcSampleTotalRows,
        applyTotalRowsByPanelDefinition,
        applyTotalRowsBySectionDefinition
    };
}
